import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Pagination } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import PlusIcon from '../../v2/icons/PlusIcon';
import Comment from './Comment';
function CommentList({ itemsPerPage, comments, paginate, allowComment, onCommentAdd, submitOnEnter, showEmptyState, rows, }) {
    const { t } = useTranslation();
    const [pages, setPages] = useState(1);
    const [activeItems, setActiveItems] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [comment, setComment] = useState('');
    useEffect(() => {
        const commentCount = comments.length;
        if (commentCount > 0) {
            setPages(Math.ceil(commentCount / itemsPerPage));
        }
        else {
            setPages(1);
        }
    }, [itemsPerPage, comments]);
    useEffect(() => {
        setSubmitDisabled(!comment.trim().length);
    }, [comment]);
    useEffect(() => {
        if (!paginate) {
            setActivePage(1);
            setActiveItems(getComments(comments));
        }
        else {
            setActiveItems(getComments(comments).slice(0, itemsPerPage));
        }
    }, [comments, paginate]);
    function handlePaginationSelect(page) {
        const offset = (page - 1) * itemsPerPage;
        const updatedActiveItems = [...comments]
            .sort((a, b) => moment(b.timestamp) - moment(a.timestamp))
            .slice(offset, offset + itemsPerPage);
        setActiveItems(updatedActiveItems);
        setActivePage(page);
    }
    function getComments(visibleComments) {
        return [...visibleComments]
            .filter((c) => c.message.length > 0)
            .sort((a, b) => moment(b.timestamp) - moment(a.timestamp));
    }
    function handleAddComment() {
        if (submitDisabled) {
            return;
        }
        onCommentAdd(encodeURI(comment));
        setComment('');
        setSubmitDisabled(true);
    }
    function handleCommentChange(e) {
        const updatedComment = e.target.value || '';
        const submitDisabled = updatedComment.trim().length === 0;
        setSubmitDisabled(submitDisabled);
        setComment(updatedComment);
    }
    function handleKeyPress(e) {
        const updatedComment = (comment || '').trim();
        if (e.altKey && e.key === 'Enter') {
            setComment(updatedComment + '\r\n');
        }
        else if (!e.shiftKey &&
            e.key === 'Enter' &&
            comment.length > 0 &&
            submitOnEnter) {
            handleAddComment();
        }
    }
    function renderComments() {
        const visibleComments = paginate ? activeItems : comments;
        if (visibleComments.length === 0) {
            if (!showEmptyState) {
                return null;
            }
            return (React.createElement("div", { className: "p-4" },
                React.createElement("p", null, t('NO_COMMENTS_FOUND'))));
        }
        return (React.createElement("div", { className: "comments-wrapper" },
            activeItems.map((comment, i) => (React.createElement(Comment, { key: i, comment: comment }))),
            paginate && pages > 1 && renderPagination()));
    }
    function renderPagination() {
        return (React.createElement("div", { className: "flex justify-center" },
            React.createElement(Pagination, { first: true, last: true, ellipsis: true, items: pages, maxButtons: 10, activePage: activePage, onSelect: handlePaginationSelect })));
    }
    return (React.createElement("div", null,
        allowComment && (React.createElement("div", { className: "p-4 bg-grey-lighter rounded" },
            React.createElement("label", { className: "form-label mb-2 block" }, titleize(t('COMMENT'))),
            React.createElement("textarea", { className: "w-full form-field resize-none mb-4", rows: rows, type: "text", value: comment, placeholder: t('ADD_A_COMMENT'), onChange: handleCommentChange, onKeyPress: handleKeyPress, "data-testid": "add-comment-textarea" }),
            React.createElement("div", { className: "flex justify-end" },
                React.createElement("button", { type: "submit", className: "button button-green", disabled: submitDisabled, onClick: handleAddComment },
                    React.createElement("div", { className: "flex items-center space-x-1" },
                        React.createElement(PlusIcon, null),
                        React.createElement("span", null, t('COMMENT'))))),
            submitOnEnter && (React.createElement("div", { className: "flex justify-end" },
                React.createElement("p", { className: "mt-2 text-xs text-gray-500" },
                    React.createElement(Trans, { i18nKey: "SHIFT_PLUS_ENTER_FOR_NEWLINE", components: [React.createElement("strong", { className: "font-medium" })] })))))),
        renderComments()));
}
CommentList.propTypes = {
    comments: PropTypes.array,
    itemsPerPage: PropTypes.number,
    paginate: PropTypes.bool,
    allowComment: PropTypes.bool,
    showEmptyState: PropTypes.bool,
    submitOnEnter: PropTypes.bool,
    rows: PropTypes.number,
};
CommentList.defaultProps = {
    comments: [],
    itemsPerPage: 5,
    paginate: false,
    allowComment: true,
    showEmptyState: true,
    submitOnEnter: true,
    rows: 5,
};
export default CommentList;
